import type {
  BlockOf,
  BlockType,
  Column,
  EditorMode,
  EmailDocument,
  EmailSettings,
  Group,
  Row,
  SaveStatus,
  Selection,
  TemplateId,
  TextBlockTarget,
} from "../types";
import type { RowInsertTarget } from "../mutations/index";
import type { ColumnPresetId } from "../column-presets";
import type { SnippetId } from "../snippets";
import {
  EDITOR_ADD_COLUMN,
  EDITOR_COPY_SELECTED_BLOCKS,
  EDITOR_CUT_SELECTED_BLOCKS,
  EDITOR_DELETE_GROUP,
  EDITOR_DELETE_SELECTED,
  EDITOR_DUPLICATE_SELECTED,
  EDITOR_INSERT_BLOCK,
  EDITOR_INSERT_GROUP,
  EDITOR_INSERT_ROW,
  EDITOR_INSERT_ROW_WITH_BLOCK,
  EDITOR_INSERT_SNIPPET,
  EDITOR_LOAD_DOCUMENT,
  EDITOR_MOVE_BLOCK,
  EDITOR_MOVE_BLOCK_TO_NEW_ROW,
  EDITOR_MOVE_ROW,
  EDITOR_MOVE_ROW_BETWEEN_PARENTS,
  EDITOR_MOVE_ROW_IN_GROUP,
  EDITOR_MOVE_SELECTED,
  EDITOR_MOVE_TOP_LEVEL_SECTION,
  EDITOR_MOVE_CONTIGUOUS_TOP_LEVEL_ROWS,
  EDITOR_MOVE_CONTIGUOUS_GROUP_ROWS,
  EDITOR_PASTE_BLOCKS_FROM_CLIPBOARD,
  EDITOR_REDO,
  EDITOR_REMOVE_COLUMN,
  EDITOR_SET_COLUMN_WIDTHS,
  EDITOR_SET_BLOCK_SELECTION,
  EDITOR_SET_ROW_SELECTION,
  EDITOR_SET_MODE,
  EDITOR_SET_SAVE_STATUS,
  EDITOR_SET_SELECTION,
  EDITOR_SET_TEXT_EDITING_BLOCK,
  EDITOR_SET_TEXT_EDIT_REENTRY_GUARD,
  EDITOR_UNDO,
  EDITOR_UNGROUP,
  EDITOR_GROUP_ROWS,
  EDITOR_UPDATE_BLOCK,
  EDITOR_UPDATE_COLUMN,
  EDITOR_UPDATE_GROUP,
  EDITOR_UPDATE_ROW,
  EDITOR_UPDATE_SETTINGS,
  type EditorAction,
} from "./types";
import type { BlockMultiSelection } from "../selection/block-multi-selection";
import type { RowMultiSelection } from "../selection/row-multi-selection";

export const setMode = (mode: EditorMode): EditorAction => ({
  type: EDITOR_SET_MODE,
  payload: mode,
});

export const setSelection = (selection: Selection): EditorAction => ({
  type: EDITOR_SET_SELECTION,
  payload: selection,
});

export const setBlockSelection = (
  blockSelection: BlockMultiSelection,
): EditorAction => ({
  type: EDITOR_SET_BLOCK_SELECTION,
  payload: blockSelection,
});

export const setRowSelection = (
  rowSelection: RowMultiSelection,
): EditorAction => ({
  type: EDITOR_SET_ROW_SELECTION,
  payload: rowSelection,
});

export const setTextEditingBlock = (
  target: TextBlockTarget | null,
): EditorAction => ({
  type: EDITOR_SET_TEXT_EDITING_BLOCK,
  payload: target,
});

export const setTextEditReentryGuard = (
  guard: string | null,
): EditorAction => ({
  type: EDITOR_SET_TEXT_EDIT_REENTRY_GUARD,
  payload: guard,
});

export const setSaveStatus = (status: SaveStatus): EditorAction => ({
  type: EDITOR_SET_SAVE_STATUS,
  payload: status,
});

export const loadDocument = (
  document: EmailDocument,
  templateId: TemplateId,
): EditorAction => ({
  type: EDITOR_LOAD_DOCUMENT,
  payload: { document, templateId },
});

export const undo = (): EditorAction => ({ type: EDITOR_UNDO });
export const redo = (): EditorAction => ({ type: EDITOR_REDO });

export const updateSettings = (
  patch: Partial<EmailSettings>,
): EditorAction => ({
  type: EDITOR_UPDATE_SETTINGS,
  payload: patch,
});

export const updateRow = (
  rowId: string,
  patch: Partial<Row>,
): EditorAction => ({
  type: EDITOR_UPDATE_ROW,
  payload: { rowId, patch },
});

export const updateGroup = (
  groupId: string,
  patch: Partial<Group>,
): EditorAction => ({
  type: EDITOR_UPDATE_GROUP,
  payload: { groupId, patch },
});

export const updateColumn = (
  rowId: string,
  columnId: string,
  patch: Partial<Column>,
): EditorAction => ({
  type: EDITOR_UPDATE_COLUMN,
  payload: { rowId, columnId, patch },
});

export const updateBlock = <T extends BlockType>(
  rowId: string,
  columnId: string,
  blockId: string,
  patch: Partial<BlockOf<T>>,
  options?: { history?: boolean },
): EditorAction => ({
  type: EDITOR_UPDATE_BLOCK,
  payload: { rowId, columnId, blockId, patch, history: options?.history },
});

export const insertBlock = (
  rowId: string,
  columnId: string,
  index: number,
  blockType: BlockType,
): EditorAction => ({
  type: EDITOR_INSERT_BLOCK,
  payload: { rowId, columnId, index, blockType },
});

export const moveBlock = (
  from: {
    rowId: string;
    columnId: string;
    blockId: string;
    blockIds?: string[];
  },
  to: { rowId: string; columnId: string; index: number },
): EditorAction => ({
  type: EDITOR_MOVE_BLOCK,
  payload: { from, to },
});

export const insertRowWithBlock = (
  target: RowInsertTarget,
  blockType: BlockType,
): EditorAction => ({
  type: EDITOR_INSERT_ROW_WITH_BLOCK,
  payload: { target, blockType },
});

export const moveBlockToNewRow = (
  from: { rowId: string; columnId: string; blockId: string },
  target: RowInsertTarget,
): EditorAction => ({
  type: EDITOR_MOVE_BLOCK_TO_NEW_ROW,
  payload: { from, target },
});

export const insertRow = (
  target: RowInsertTarget,
  presetId: ColumnPresetId,
): EditorAction => ({
  type: EDITOR_INSERT_ROW,
  payload: { target, presetId },
});

export const insertGroup = (index: number, group?: Group): EditorAction => ({
  type: EDITOR_INSERT_GROUP,
  payload: { index, group },
});

export const insertSnippet = (
  snippetId: SnippetId,
  index?: number,
): EditorAction => ({
  type: EDITOR_INSERT_SNIPPET,
  payload: { snippetId, index },
});

export const moveRow = (fromIndex: number, toIndex: number): EditorAction => ({
  type: EDITOR_MOVE_ROW,
  payload: { fromIndex, toIndex },
});

export const moveTopLevelSection = (
  fromIndex: number,
  toIndex: number,
): EditorAction => ({
  type: EDITOR_MOVE_TOP_LEVEL_SECTION,
  payload: { fromIndex, toIndex },
});

export const moveContiguousTopLevelRows = (
  rowIds: string[],
  slotIndex: number,
): EditorAction => ({
  type: EDITOR_MOVE_CONTIGUOUS_TOP_LEVEL_ROWS,
  payload: { rowIds, slotIndex },
});

export const moveContiguousGroupRows = (
  groupId: string,
  rowIds: string[],
  slotIndex: number,
): EditorAction => ({
  type: EDITOR_MOVE_CONTIGUOUS_GROUP_ROWS,
  payload: { groupId, rowIds, slotIndex },
});

export const moveRowInGroup = (
  groupId: string,
  fromIndex: number,
  toIndex: number,
): EditorAction => ({
  type: EDITOR_MOVE_ROW_IN_GROUP,
  payload: { groupId, fromIndex, toIndex },
});

export const moveRowBetweenParents = (
  rowId: string,
  to:
    | { parent: 'document'; index: number }
    | { parent: 'group'; groupId: string; rowIndex: number },
): EditorAction => ({
  type: EDITOR_MOVE_ROW_BETWEEN_PARENTS,
  payload: { rowId, to },
});

export const ungroup = (groupId: string): EditorAction => ({
  type: EDITOR_UNGROUP,
  payload: groupId,
});

export const groupRows = (rowIds: string[]): EditorAction => ({
  type: EDITOR_GROUP_ROWS,
  payload: rowIds,
});

export const deleteGroup = (
  groupId: string,
  mode: 'delete' | 'ungroup',
): EditorAction => ({
  type: EDITOR_DELETE_GROUP,
  payload: { groupId, mode },
});

export const setColumnWidths = (
  rowId: string,
  widths: Row['columnWidths'],
  options?: { history?: boolean },
): EditorAction => ({
  type: EDITOR_SET_COLUMN_WIDTHS,
  payload: { rowId, widths, history: options?.history },
});

export const addColumn = (
  rowId: string,
  afterColumnId?: string,
): EditorAction => ({
  type: EDITOR_ADD_COLUMN,
  payload: { rowId, afterColumnId },
});

export const removeColumn = (
  rowId: string,
  columnId: string,
): EditorAction => ({
  type: EDITOR_REMOVE_COLUMN,
  payload: { rowId, columnId },
});

export const duplicateSelected = (): EditorAction => ({
  type: EDITOR_DUPLICATE_SELECTED,
});
export const deleteSelected = (): EditorAction => ({
  type: EDITOR_DELETE_SELECTED,
});
export const moveSelected = (direction: -1 | 1): EditorAction => ({
  type: EDITOR_MOVE_SELECTED,
  payload: direction,
});
export const copySelectedBlocks = (): EditorAction => ({
  type: EDITOR_COPY_SELECTED_BLOCKS,
});
export const cutSelectedBlocks = (): EditorAction => ({
  type: EDITOR_CUT_SELECTED_BLOCKS,
});
export const pasteBlocksFromClipboard = (): EditorAction => ({
  type: EDITOR_PASTE_BLOCKS_FROM_CLIPBOARD,
});
